import { useState } from 'react';

import { api, ApiError } from '../../api';
import { go, goReplace } from '../../App';
import { useLoad, usePolled } from '../../data';
import { ConnectionState, Loading, MoneyField, PageHead, Panel, Stars, formatWhen } from '../../ui';
import { ReviewAggregate } from '../../types';
import {
  ASKED,
  askedWords,
  tripOfferLine,
  type Told,
  INVITER,
  INVITE_EMAIL,
  INVITE_GREEK_ONLY,
  INVITE_SIMULATED,
  INVITE_TRIP,
  NOT_NOW_ROW,
  RESEND,
  WITHDRAW,
  athensDay,
  athensWhen,
  channelRefused,
  inviteOnItsWay,
  inviteSimulatedBody,
  TRIP_NOT_LISTED,
  joinedOnOtherNumber,
  notNowHint,
  resentWords,
  tellThemItsComing,
  textSimulatedToo,
  type Channel,
} from '../../joining';
import { chosenMinor, readGuests, readHours } from '../../price';

// H4 — the supplier directory and the invite form. The directory is the
// hotel's working list: active suppliers, requested connections awaiting the
// supplier's approval, and invitations still in flight — including
// `declined to join` and `this reached the wrong person`, which are stories
// the hotel may need to act on, not rows to hide.
//
// B35: a supplier who said yes to the hotel's invitation and is still setting
// up reads "Setting up", apart from one asked to connect — the API works it out
// from the hotel's own rows. N40: "Not now" reads in kinder words than "declined
// to join", the form asks once for the inviter's name and says only Greek mobiles
// get codes, and after sending it suggests a call and names the sender. B19 + B31: an invitation says what its email did —
// never "sent" for one that was refused or only simulated — and Re-send says
// its error instead of swallowing it.

interface DirectoryItem {
  kind: 'connection' | 'invitation';
  supplierAccountId?: string;
  inviteId?: string;
  name: string;
  email?: string;
  phone?: string;
  status: 'active' | 'setting-up' | 'requested' | 'invited' | 'declined' | 'ended' | 'burned';
  invitedAt?: string;
  lastSentAt?: string;
  expired?: boolean;
  // Invitations: what the last send did (B19) — the best of the text and the
  // email since N40 part 2. Absent on an older invitation.
  delivery?: 'sent' | 'simulated' | 'failed' | 'not-sent';
  // Each channel of the last send (N40), so a refused one is never hidden.
  sms?: Channel;
  emailDelivery?: Channel;
  // A "not now": the one reminder, as it truly stands (N40).
  followUp?: { state: 'due' | 'sent' | 'simulated' | 'unknown' | 'failed' | 'none'; at?: string };
  // Whether Re-send may go now (N40 fix round: twice in 30 days, a day apart).
  resend?: { state: 'ok' | 'wait' | 'twice'; at?: string };
  // Burned by the hotel's own Withdraw (not "This is not me").
  withdrawn?: boolean;
  // A connection whose yes came from a number other than the one typed (S-C).
  joinedOnOtherNumber?: string;
  // Already open when they said yes on the link: the invitation's trip was not listed.
  tripNotListed?: boolean;
  // The supplier's review aggregate (DECISION-056) — present on connection rows,
  // read from the supplier's own profile, never their bookings (DECISION-020).
  rating?: ReviewAggregate;
  // N42 — a connection the hotel asked for: when, what reached the supplier, the
  // call prompt after 5 days (on the number it typed), when they answered, and
  // how the trip it offered stands.
  askedAt?: string;
  told?: Told;
  callPrompt?: boolean;
  answeredAt?: string;
  tripOffer?: { name: string; state: 'with-them' | 'listed' | 'kept-own' | 'left' | 'not-listed' };
}

const FIVE_DAYS_MS = 5 * 86_400_000;

// Label plus the connection-state kind the shared <ConnectionState> chip draws
// (canon 04 "Connection state") — the hotel's wording; the colour is the widget's.
const STATUS_COPY: Record<DirectoryItem['status'], { label: string; kind: 'connected' | 'pending' | 'dormant' }> = {
  active: { label: 'Active', kind: 'connected' },
  'setting-up': { label: 'Setting up', kind: 'pending' },
  requested: { label: ASKED.requested, kind: 'pending' },
  invited: { label: 'Invited', kind: 'pending' },
  declined: { label: NOT_NOW_ROW.label, kind: 'dormant' },
  // N42: the supplier ended it — kind words, the history kept.
  ended: { label: ASKED.ended, kind: 'dormant' },
  burned: { label: 'Invitation reached the wrong person', kind: 'dormant' },
};

export function Suppliers({ invite }: { invite?: boolean }) {
  const { data, error, reload } = usePolled<{ items: DirectoryItem[] }>('/suppliers');
  const items = data ? data.items : null;

  if (invite) return <InviteForm onDone={() => { reload(); goReplace('/suppliers'); }} />;

  return (
    <div>
      <PageHead title="Suppliers" sub="Who you can book, and who you have asked.">
        <a href="#/suppliers/invite" className="btn" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
          Invite supplier
        </a>
      </PageHead>

      {error && items === null && <p className="error-line">{error}</p>}
      {items === null ? (
        <Loading />
      ) : items.length === 0 ? (
        <div className="empty-neutral">No suppliers yet — invite the ones you already work with.</div>
      ) : (
        <Panel flush>
          <table>
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Status</th>
                <th>Since</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.supplierAccountId ?? s.inviteId}>
                  <td>
                    {s.supplierAccountId ? (
                      <a href={`#/suppliers/${s.supplierAccountId}`} style={{ fontWeight: 600 }}>
                        {s.name}
                      </a>
                    ) : (
                      s.name
                    )}
                    {s.email && (
                      <>
                        <br />
                        <span className="hint">{s.email}</span>
                      </>
                    )}
                    {/* The supplier's rating (DECISION-056) — shown once they've
                        been reviewed, so the hotel sees who is dependable. */}
                    {s.rating && s.rating.count > 0 && (
                      <>
                        <br />
                        <span className="hint">
                          <Stars rating={s.rating.average} /> {s.rating.average.toFixed(1)} ({s.rating.count})
                        </span>
                      </>
                    )}
                  </td>
                  <td>
                    <ConnectionState kind={statusOf(s).kind} label={statusOf(s).label} />
                    {s.kind === 'invitation' && s.status === 'declined' && (
                      <span className="hint" style={{ display: 'block', marginTop: 'var(--space-xs)' }}>
                        {notNowHint(s.followUp, athensDay)}
                      </span>
                    )}
                    {/* N42 — what reached them about the request, and their answer, in kind words. */}
                    {/* The number is offered once: in the call prompt when it shows,
                        else in the line (design review CONSIDER 2). */}
                    {s.kind === 'connection' && s.status === 'requested' && (
                      <span className="hint" style={{ display: 'block', marginTop: 'var(--space-xs)' }}>
                        {ASKED.told(s.told, athensWhen, s.callPrompt ? undefined : s.phone)}
                      </span>
                    )}
                    {s.kind === 'connection' && s.status === 'requested' && s.callPrompt && s.phone && (
                      <span className="hint" style={{ display: 'block', marginTop: 'var(--space-xs)' }}>
                        {ASKED.call} <a href={`tel:${s.phone}`}>{s.phone}</a>
                      </span>
                    )}
                    {s.kind === 'connection' && s.status === 'declined' && (
                      <span className="hint" style={{ display: 'block', marginTop: 'var(--space-xs)' }}>
                        {ASKED.declinedHint(s.answeredAt ? athensDay(s.answeredAt) : 'a recent day')}
                      </span>
                    )}
                    {s.kind === 'connection' && s.status === 'ended' && (
                      <span className="hint" style={{ display: 'block', marginTop: 'var(--space-xs)' }}>
                        {ASKED.endedHint(s.answeredAt ? athensDay(s.answeredAt) : 'a recent day')}
                      </span>
                    )}
                    {s.tripOffer && tripOfferLine(s.tripOffer) && (
                      <span className="hint" style={{ display: 'block', marginTop: 'var(--space-xs)' }}>
                        {tripOfferLine(s.tripOffer)}
                      </span>
                    )}
                    {s.status === 'setting-up' && (
                      <span className="hint" style={{ display: 'block', marginTop: 'var(--space-xs)' }}>
                        They said yes and are setting up. Once they finish, list their trip and you can book them.
                      </span>
                    )}
                    {s.tripNotListed && !s.tripOffer && (
                      <span className="hint" style={{ display: 'block', marginTop: 'var(--space-xs)' }}>
                        {TRIP_NOT_LISTED}
                      </span>
                    )}
                    {s.joinedOnOtherNumber && (
                      <span className="hint" style={{ display: 'block', marginTop: 'var(--space-xs)' }}>
                        {joinedOnOtherNumber(s.joinedOnOtherNumber)}
                      </span>
                    )}
                    {s.status === 'burned' && s.withdrawn && (
                      <span className="hint" style={{ display: 'block', marginTop: 'var(--space-xs)' }}>
                        {WITHDRAW.rowHint}
                      </span>
                    )}
                    {/* A channel refused beside the one that reached them (N40 review). */}
                    {s.status === 'invited' && s.delivery !== 'failed' && s.sms?.state === 'failed' && (
                      <span className="hint" style={{ display: 'block', marginTop: 'var(--space-xs)' }}>
                        {channelRefused('text', s.sms.reason)}
                      </span>
                    )}
                    {s.status === 'invited' && s.delivery !== 'failed' && s.emailDelivery?.state === 'failed' && (
                      <span className="hint" style={{ display: 'block', marginTop: 'var(--space-xs)' }}>
                        {channelRefused('email', s.emailDelivery.reason)}
                      </span>
                    )}
                    {s.status === 'invited' && (s.delivery === 'failed' || s.delivery === 'not-sent') && (
                      <span className="hint" style={{ display: 'block', marginTop: 'var(--space-xs)' }}>
                        {s.delivery === 'failed' ? 'The invitation could not be sent' : 'Nothing has gone yet'} — Re-send
                        {s.phone ? (
                          <>
                            , or <a href={`tel:${s.phone}`}>call {s.phone}</a>
                          </>
                        ) : null}
                        .
                      </span>
                    )}
                    {/* No response in 5 days → the hotel is prompted to
                        call, the number one tap away (H4's edge). */}
                    {s.status === 'invited' && !s.expired && s.lastSentAt &&
                      Date.now() - Date.parse(s.lastSentAt) > FIVE_DAYS_MS && s.phone && (
                        <span className="hint" style={{ display: 'block', marginTop: 'var(--space-xs)' }}>
                          {/* One H4 wording for both rows (design review CONSIDER 2). */}
                          {ASKED.call} <a href={`tel:${s.phone}`}>{s.phone}</a>
                        </span>
                      )}
                  </td>
                  {/* Never broken across lines by a long name beside them (design review). */}
                  {/* A request's own day too, so the hotel can see the five days for itself (design review CONSIDER 3). */}
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                    {s.invitedAt ? formatWhen(s.invitedAt).split('·')[0] : s.askedAt ? formatWhen(s.askedAt).split('·')[0] : '—'}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {s.kind === 'invitation' && s.status !== 'burned' && (
                      <>
                        <ResendButton item={s} onSent={reload} />
                        <WithdrawButton inviteId={s.inviteId!} name={s.name} onDone={reload} />
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}

// The invitation's state as the hotel reads it. An invitation's email is said
// as it happened (B19): refused, not sent yet, or only simulated here — never
// "Invited" with a fresh stamp for an email that never left.
// A delivery failure is the hotel's to act on, so it is not the closed-looking
// dormant chip (stone on stone, under AA): it is pending, and the hint line
// under it carries the action — Re-send, or call.
function statusOf(s: DirectoryItem): { kind: 'connected' | 'pending' | 'dormant'; label: string } {
  if (s.status === 'invited') {
    if (s.delivery === 'failed') return { kind: 'pending', label: 'Invitation not delivered' };
    if (s.delivery === 'not-sent') return { kind: 'pending', label: 'Invitation not sent yet' };
    if (s.expired) return { kind: 'pending', label: 'Invited · link expired' };
    if (s.delivery === 'simulated') return { kind: 'pending', label: 'Invited · simulated here' };
  }
  if (s.status === 'burned' && s.withdrawn) return { kind: 'dormant', label: WITHDRAW.row };
  // N42: a connection request the supplier said not now to, in kind words.
  if (s.kind === 'connection' && s.status === 'declined') return { kind: 'dormant', label: ASKED.declined };
  return STATUS_COPY[s.status];
}

// "Expired → re-issued by the hotel in one click, never by support"
// (canon 11's edge): the stored three fields go straight back to the API.
// B31: a refused Re-send says so here — it used to swallow the error, and the
// row was stamped as sent again — and the directory re-reads either way, so the
// row shows what the send did.
function ResendButton({ item, onSent }: { item: DirectoryItem; onSent: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [said, setSaid] = useState('');
  const send = async () => {
    setBusy(true);
    setError('');
    setSaid('');
    try {
      const res = await api<{ kind: string; delivery?: 'sent' | 'simulated'; sms?: Channel; email?: Channel }>('POST', '/suppliers/invitations', {
        businessName: item.name,
        ...(item.email ? { email: item.email } : {}),
        phone: item.phone,
      });
      setSaid(
        res.delivery === 'simulated'
          ? INVITE_SIMULATED
          : res.delivery === 'sent'
            ? resentWords({ phone: item.phone, email: item.email, sms: res.sms, mail: res.email })
            : '',
      );
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      await onSent();
      setBusy(false);
    }
  };
  const cannot = item.resend?.state === 'twice' ? RESEND.twice(item.phone) : item.resend?.state === 'wait' && item.resend.at ? RESEND.wait(athensWhen(item.resend.at)) : '';
  return (
    <>
      <button className="btn secondary" disabled={busy || !item.phone || !!cannot} onClick={send}>
        {busy ? RESEND.sending : RESEND.label}
      </button>
      {cannot && (
        <span className="hint" style={{ display: 'block', marginTop: 'var(--space-xs)', whiteSpace: 'normal' }}>
          {cannot}
        </span>
      )}
      {said && (
        <span className="hint" role="status" style={{ display: 'block', marginTop: 'var(--space-xs)' }}>
          {said}
        </span>
      )}
      {error && (
        <p className="error-line" role="alert" style={{ marginTop: 'var(--space-xs)' }}>
          {error}
        </p>
      )}
    </>
  );
}

// Withdraw (N40 fix round, S-C): the hotel takes back an open invitation — its
// link stops working for good. Asked once, in words, before it goes.
function WithdrawButton({ inviteId, name, onDone }: { inviteId: string; name: string; onDone: () => Promise<void> }) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const withdraw = async () => {
    setBusy(true);
    setError('');
    try {
      await api('POST', `/suppliers/invitations/${encodeURIComponent(inviteId)}/withdraw`, {});
      setAsking(false);
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      await onDone();
      setBusy(false);
    }
  };
  if (!asking) {
    return (
      <button className="btn ghost" style={{ marginLeft: 'var(--space-xs)' }} onClick={() => setAsking(true)}>
        {WITHDRAW.label}
      </button>
    );
  }
  return (
    <span style={{ display: 'block', marginTop: 'var(--space-xs)', whiteSpace: 'normal' }}>
      <span className="hint" style={{ display: 'block' }}>{WITHDRAW.confirm(name)}</span>
      <button className="btn secondary" disabled={busy} onClick={withdraw}>
        {busy ? WITHDRAW.busy : WITHDRAW.confirmYes}
      </button>
      <button className="btn ghost" style={{ marginLeft: 'var(--space-xs)' }} disabled={busy} onClick={() => setAsking(false)}>
        {WITHDRAW.keep}
      </button>
      {error && (
        <p className="error-line" role="alert" style={{ marginTop: 'var(--space-xs)' }}>
          {error}
        </p>
      )}
    </span>
  );
}

// A few fields, and those are the whole gate (H4). An invalid number is
// caught before sending — by the API, whose word is final. N39 (DECISION-080
// §7): optionally the trip the hotel already books with them — what it pays
// them for it today, before its margin, never the guest price — and its margin
// on it, which stays on the hotel's own records and is never shown to the
// supplier. With it, the supplier's setup opens on the trip and Finish lists it.
function InviteForm({ onDone }: { onDone: () => void }) {
  // N40: the person, as the supplier knows them — asked once, remembered on the
  // hotel user's own record (GET /v1/account user.name), named on the link.
  const me = useLoad<{ user?: { name?: string } }>('/account');
  const savedName = me.data?.user?.name ?? '';
  const [nameOpen, setNameOpen] = useState(false);
  const [inviterName, setInviterName] = useState('');
  const askName = !savedName || nameOpen;
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [tripName, setTripName] = useState('');
  const [tripKind, setTripKind] = useState<'experience' | 'transfer' | undefined>(undefined);
  const [tripPrice, setTripPrice] = useState('');
  const [tripPriceChoice, setTripPriceChoice] = useState<number | null>(null);
  // Asked, never assumed — neither starts selected.
  const [tripPer, setTripPer] = useState<'guest' | 'job' | undefined>(undefined);
  const [tripHours, setTripHours] = useState('');
  const [tripGuests, setTripGuests] = useState('');
  const [marginText, setMarginText] = useState('');
  const [marginChoice, setMarginChoice] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState<'invited' | 'connection-request' | 'already-connected' | 'already-asked' | 'setting-up' | null>(null);
  // N42: a request to a number already on ISO — asked again, and what reaches them.
  const [asked, setAsked] = useState<{ again?: boolean; told?: Told }>({});
  // What each channel did (N40 part 2): the text and, when given, the email.
  const [channels, setChannels] = useState<{ sms?: Channel; email?: Channel }>({});
  // What the invitation's sends did (B19): 'sent' — one reached a person; 'simulated' —
  // this environment's demo inbox, nothing left.
  const [delivery, setDelivery] = useState<'sent' | 'simulated' | undefined>(undefined);
  // N39 review: false when the number is already on ISO and the trip entered was
  // not carried over — said plainly, so the hotel lists it itself.
  const [tripCarried, setTripCarried] = useState<boolean | undefined>(undefined);
  // Other open invitations from this hotel to the same business or email under
  // another number — offered to withdraw, never withdrawn silently (S-C).
  const [othersOpen, setOthersOpen] = useState<{ inviteId: string; name: string; phoneHint?: string }[]>([]);
  const [withdrawn, setWithdrawn] = useState<string[]>([]);

  const priceMinor = chosenMinor(tripPrice, tripPriceChoice);
  const marginMinor = chosenMinor(marginText, marginChoice);
  const hours = tripHours.trim() ? readHours(tripHours) : undefined;
  const guests = tripGuests.trim() ? readGuests(tripGuests) : undefined;
  const anyTrip = !!(tripName.trim() || tripKind || tripPrice.trim() || tripPer || tripHours.trim() || tripGuests.trim());
  const tripComplete = !!tripName.trim() && !!tripKind && priceMinor !== undefined && priceMinor > 0 && !!tripPer;
  const tripProblem = !anyTrip
    ? marginText.trim()
      ? INVITE_TRIP.marginNeedsTrip
      : ''
    : !tripComplete
      ? INVITE_TRIP.incomplete
      : (tripHours.trim() && hours === undefined) || (tripGuests.trim() && guests === undefined) || (marginText.trim() && marginMinor === undefined)
        ? INVITE_TRIP.incomplete
        : '';

  const send = async () => {
    if (tripProblem) {
      setError(tripProblem);
      return;
    }
    setBusy(true);
    setError('');
    const body: Record<string, unknown> = { businessName, phone, ...(email.trim() ? { email: email.trim() } : {}) };
    if (askName && inviterName.trim()) body.inviterName = inviterName.trim();
    if (anyTrip && tripComplete && priceMinor !== undefined) {
      body.firstProduct = {
        name: tripName.trim(),
        kind: tripKind,
        per: tripPer,
        supplierPrice: { minor: priceMinor, currency: 'EUR' },
        ...(hours !== undefined ? { durationHours: hours } : {}),
        ...(guests !== undefined ? { maxGuests: guests } : {}),
      };
      // Beside the trip, never inside it: the margin is the hotel's alone.
      if (marginMinor !== undefined) body.firstProductMargin = { minor: marginMinor, currency: 'EUR' };
    }
    try {
      const res = await api<{
        kind: 'invited' | 'connection-request' | 'already-connected' | 'already-asked' | 'setting-up';
        again?: boolean;
        told?: Told;
        delivery?: 'sent' | 'simulated';
        sms?: Channel;
        email?: Channel;
        tripCarried?: boolean;
        othersOpen?: { inviteId: string; name: string; phoneHint?: string }[];
      }>('POST', '/suppliers/invitations', body);
      setDelivery(res.delivery);
      setChannels({ sms: res.sms, email: res.email });
      setTripCarried(res.tripCarried);
      setOthersOpen(res.othersOpen ?? []);
      setAsked({ again: res.again, told: res.told });
      setSent(res.kind);
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    // Said as it happened (B19): "sent" only when the text or the email went;
    // in an environment that simulates them, the honest words say so.
    const simulated = sent === 'invited' && delivery === 'simulated';
    return (
      <div>
        <PageHead
          title={
            simulated
              ? 'Invitation recorded'
              : sent === 'invited'
                ? 'Invitation sent'
                : sent === 'connection-request' || sent === 'already-asked'
                  ? 'Asked to connect'
                  : sent === 'setting-up'
                    ? 'Setting up'
                    : 'Already connected'
          }
          sub=""
        />
        <Panel>
          <p style={{ marginBottom: 'var(--space-md)' }}>
            {sent === 'invited'
              ? simulated
                ? inviteSimulatedBody({ phone: phone.trim(), email: email.trim() || undefined, sms: channels.sms, mail: channels.email })
                : inviteOnItsWay({ business: businessName, phone: phone.trim(), email: email.trim() || undefined, sms: channels.sms, mail: channels.email })
              : sent === 'connection-request' || sent === 'already-asked' || sent === 'setting-up'
                ? askedWords(businessName, { ...asked, tripCarried, already: sent === 'already-asked', settingUp: sent === 'setting-up', phone: phone.trim() })
                : `${businessName} is already connected with you.`}
          </p>
          {/* What did not go, right after what did (B31) — before the call. */}
          {sent === 'invited' && channels.sms?.state === 'failed' && (
            <p className="hint" role="status" style={{ marginBottom: 'var(--space-md)' }}>
              {channelRefused('text', channels.sms.reason)}
            </p>
          )}
          {sent === 'invited' && channels.email?.state === 'failed' && (
            <p className="hint" role="status" style={{ marginBottom: 'var(--space-md)' }}>
              {channelRefused('email', channels.email.reason)}
            </p>
          )}
          {/* The email went, the text only to this environment's demo inbox: said too. */}
          {sent === 'invited' && !simulated && channels.sms?.state === 'simulated' && (
            <p className="hint" role="status" style={{ marginBottom: 'var(--space-md)' }}>
              {textSimulatedToo(phone.trim())}
            </p>
          )}
          {sent === 'invited' && !simulated && (
            <p className="hint" role="status" style={{ marginBottom: 'var(--space-md)' }}>
              {tellThemItsComing(
                businessName,
                channels.sms?.state === 'sent' && channels.email?.state === 'sent' ? 'both' : channels.sms?.state === 'sent' ? 'text' : 'email',
              )}
            </p>
          )}
          {othersOpen
            .filter((o) => !withdrawn.includes(o.inviteId))
            .map((o) => (
              <div key={o.inviteId} style={{ marginBottom: 'var(--space-md)' }}>
                <p className="hint" role="status">{WITHDRAW.offer(o.name, o.phoneHint)}</p>
                <WithdrawButton inviteId={o.inviteId} name={o.name} onDone={async () => setWithdrawn((w) => [...w, o.inviteId])} />
              </div>
            ))}
          {tripCarried === false && (
            <p className="hint" role="status" style={{ marginBottom: 'var(--space-md)' }}>
              {/* Said for what is true of each answer (correctness SHOULD 5). */}
              {sent === 'connection-request'
                ? INVITE_TRIP.notCarried(businessName)
                : sent === 'already-asked'
                  ? INVITE_TRIP.notCarriedAsked(businessName)
                  : sent === 'setting-up'
                    ? INVITE_TRIP.notCarriedSettingUp(businessName)
                    : INVITE_TRIP.notCarriedConnected(businessName)}
            </p>
          )}
          <button className="btn" onClick={onDone}>Back to suppliers</button>
        </Panel>
      </div>
    );
  }

  return (
    <div>
      <PageHead title="Invite a supplier" sub={INVITE_TRIP.pageSub} />
      <Panel>
        {askName ? (
          <div className="field">
            <label htmlFor="iname">{INVITER.label}</label>
            <input
              id="iname"
              maxLength={30}
              placeholder={INVITER.placeholder}
              value={inviterName}
              onChange={(e) => setInviterName(e.target.value)}
            />
            <p className="hint" style={{ marginTop: 'var(--space-xs)' }}>
              {INVITER.hint}
            </p>
          </div>
        ) : (
          <p className="hint" style={{ marginTop: 0 }}>
            {INVITER.sentAs(savedName)}{' '}
            <button
              type="button"
              className="linklike"
              onClick={() => {
                setInviterName(savedName);
                setNameOpen(true);
              }}
            >
              {INVITER.change}
            </button>
          </p>
        )}
        <div className="field">
          <label htmlFor="bn">Business name</label>
          <input id="bn" value={businessName} onChange={(e) => setBusinessName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="ph">Mobile phone</label>
          <input id="ph" className="mono" placeholder="+30 691 234 5678" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <p className="hint" style={{ marginTop: 'var(--space-xs)' }}>
            {INVITE_GREEK_ONLY}
          </p>
        </div>
        <div className="field">
          <label htmlFor="em">{INVITE_EMAIL.label}</label>
          <input id="em" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <p className="hint" style={{ marginTop: 'var(--space-xs)' }}>
            {INVITE_EMAIL.hint}
          </p>
        </div>
      </Panel>

      <Panel title={INVITE_TRIP.title} style={{ marginTop: 'var(--space-xl)' }}>
        <p className="hint" style={{ marginTop: 0 }}>{INVITE_TRIP.lede}</p>
        <div className="field">
          <label htmlFor="it-name">{INVITE_TRIP.name}</label>
          <input id="it-name" value={tripName} maxLength={120} placeholder={INVITE_TRIP.namePlaceholder} onChange={(e) => setTripName(e.target.value)} />
        </div>
        <div className="field">
          <label>{INVITE_TRIP.kind}</label>
          <div role="radiogroup" aria-label={INVITE_TRIP.kind}>
            {(['experience', 'transfer'] as const).map((k) => (
              <button key={k} type="button" role="radio" className={`reason ${tripKind === k ? 'selected' : ''}`} aria-checked={tripKind === k} onClick={() => setTripKind(k)}>
                {k === 'experience' ? INVITE_TRIP.experience : INVITE_TRIP.transfer}
                {tripKind === k && <span className="disc" />}
              </button>
            ))}
          </div>
        </div>
        <div className="row">
          <MoneyField
            id="it-price"
            label={INVITE_TRIP.price}
            placeholder={INVITE_TRIP.pricePlaceholder}
            bad={INVITE_TRIP.priceBad}
            value={tripPrice}
            choice={tripPriceChoice}
            onChange={(v) => {
              setTripPrice(v);
              setTripPriceChoice(null);
            }}
            onChoose={setTripPriceChoice}
          />
          <div className="field">
            <label>{INVITE_TRIP.per}</label>
            <div role="radiogroup" aria-label={INVITE_TRIP.per}>
              {(['guest', 'job'] as const).map((p) => (
                <button key={p} type="button" role="radio" className={`reason ${tripPer === p ? 'selected' : ''}`} aria-checked={tripPer === p} onClick={() => setTripPer(p)}>
                  {p === 'guest' ? INVITE_TRIP.perGuest : INVITE_TRIP.perJob}
                  {tripPer === p && <span className="disc" />}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="field">
            <label htmlFor="it-hours">{INVITE_TRIP.duration}</label>
            <input id="it-hours" className="mono" inputMode="decimal" placeholder={INVITE_TRIP.durationPlaceholder} value={tripHours} onChange={(e) => setTripHours(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="it-guests">{INVITE_TRIP.guests}</label>
            <input id="it-guests" className="mono" inputMode="numeric" placeholder={INVITE_TRIP.guestsPlaceholder} value={tripGuests} onChange={(e) => setTripGuests(e.target.value)} />
          </div>
        </div>
        <MoneyField
          id="it-margin"
          label={INVITE_TRIP.margin}
          hint={INVITE_TRIP.marginHint}
          placeholder={INVITE_TRIP.marginPlaceholder}
          bad={INVITE_TRIP.priceBad}
          value={marginText}
          choice={marginChoice}
          onChange={(v) => {
            setMarginText(v);
            setMarginChoice(null);
          }}
          onChoose={setMarginChoice}
        />
      </Panel>

      {error && <p className="error-line" role="alert">{error}</p>}
      <div style={{ display: 'flex', gap: 'var(--space-sm)', marginTop: 'var(--space-lg)' }}>
        <button className="btn secondary" onClick={() => go('/suppliers')}>Cancel</button>
        <button className="btn" disabled={busy || !businessName.trim() || !phone.trim()} onClick={send}>
          {busy ? 'Sending…' : 'Send invitation'}
        </button>
      </div>
    </div>
  );
}
